import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Spin, Typography, ConfigProvider, theme as antTheme } from 'antd';
import { ThemeProvider, useTheme } from './context/ThemeContext';

import AppLayout from './components/Layout/AppLayout';
import Dashboard from './pages/Dashboard';
import Infrastructure from './pages/Infrastructure';
import SecurityGroups from './pages/SecurityGroups';
import IPSearch from './pages/IPSearch';
import Backups from './pages/Backups';
import Audit from './pages/Audit';
import Settings from './pages/Settings';
import FeatureRequests from './pages/FeatureRequests';
import HealthEvents from './pages/HealthEvents';
import MmAnalyze from './pages/MmAnalyze';
import Login from './pages/Login';
import { TOKEN_KEY, logout as simpleLogout, getTokenUser } from './api/client';

// ─── Auth mode ─────────────────────────────────────────────────────────────────
//
//   REACT_APP_AUTH_MODE=simple   username/password break-glass only (default)
//   REACT_APP_AUTH_MODE=okta     Okta SSO primary + break-glass admin fallback
//
const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'simple';

// ─── Theme ─────────────────────────────────────────────────────────────────────

const BRAND_ORANGE = '#E8741A';

function ThemedConfigProvider({ children }) {
  const { isDark } = useTheme();
  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: { colorPrimary: BRAND_ORANGE },
      }}
    >
      {children}
    </ConfigProvider>
  );
}

// ─── Shared page routes ────────────────────────────────────────────────────────
//
// Used by both SimpleAuthApp and HybridApp so the route list stays in one place.
//
function AppRoutes({ user, onSignOut }) {
  return (
    <BrowserRouter>
      <AppLayout user={user} onSignOut={onSignOut}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/infrastructure" element={<Infrastructure />} />
          <Route path="/security-groups" element={<SecurityGroups />} />
          <Route path="/security-groups/search" element={<IPSearch />} />
          <Route path="/backups" element={<Backups />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/feature-requests" element={<FeatureRequests />} />
          <Route path="/health-events" element={<HealthEvents />} />
          <Route path="/mm-analyze" element={<MmAnalyze />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </AppLayout>
    </BrowserRouter>
  );
}

// ─── Simple auth (break-glass only) ───────────────────────────────────────────

function SimpleAuthApp() {
  const [loggedIn, setLoggedIn] = useState(() => !!localStorage.getItem(TOKEN_KEY));
  const handleLogout = () => { simpleLogout(); setLoggedIn(false); };

  if (!loggedIn) {
    return <Login onSuccess={() => setLoggedIn(true)} />;
  }
  return <AppRoutes user={{ username: getTokenUser() || 'admin' }} onSignOut={handleLogout} />;
}

// ─── Hybrid auth (Okta SSO primary + break-glass admin) ───────────────────────
//
// Auth priority:
//   1. HMAC token in localStorage  → break-glass admin session (simple auth)
//   2. Active Cognito session       → SSO user session
//   3. Neither                      → login page with SSO button + admin form
//
function HybridApp() {
  const [simpleLoggedIn, setSimpleLoggedIn] = useState(
    () => !!localStorage.getItem(TOKEN_KEY)
  );
  const [cognitoUser, setCognitoUser] = useState(null);
  // 'loading' while Amplify initialises; 'ready' once we know the session state
  const [authState, setAuthState] = useState('loading');
  const signOutRef = useRef(null);

  useEffect(() => {
    let unsubscribe;

    // Capture before Amplify.configure() potentially clears the URL.
    const isOAuthCallbackUrl = window.location.search.includes('code=');

    // Persist the retry flag across page loads to prevent infinite loops.
    // On a tile-initiated callback, Amplify silently abandons the code
    // exchange (no PKCE state stored) without firing any Hub event.
    // We detect this via a watchdog, retry with signInWithRedirect() so
    // Amplify generates a proper PKCE challenge, and Cognito honours the
    // active session to issue a fresh code without prompting Okta again.
    const RETRY_KEY = 'itgi-oauth-retry';
    const hasRetried = sessionStorage.getItem(RETRY_KEY) === '1';

    const doRetry = async () => {
      if (hasRetried) {
        // Second attempt also failed — give up and show the login page.
        sessionStorage.removeItem(RETRY_KEY);
        setAuthState('ready');
        return;
      }
      sessionStorage.setItem(RETRY_KEY, '1');
      try {
        const { signInWithRedirect } = await import('aws-amplify/auth');
        signInWithRedirect(); // navigates away; remaining code won't run
      } catch (e) {
        console.error('SSO retry failed:', e);
        sessionStorage.removeItem(RETRY_KEY);
        setAuthState('ready');
      }
    };

    // Watchdog: if still in loading state 3 s after an OAuth callback that
    // Amplify didn't handle (tile flow = no stored PKCE state, no Hub event),
    // trigger the SP-initiated retry.
    let watchdog;
    if (isOAuthCallbackUrl && !hasRetried) {
      watchdog = setTimeout(doRetry, 3000);
    }

    Promise.all([
      import('aws-amplify'),
      import('aws-amplify/auth'),
      import('aws-amplify/utils'),
    ]).then(([{ Amplify }, { getCurrentUser, signOut }, { Hub }]) => {
      // Register Hub listener BEFORE Amplify.configure() to avoid missing
      // events fired immediately during or just after configuration.
      unsubscribe = Hub.listen('auth', ({ payload }) => {
        switch (payload.event) {
          case 'signedIn':
            clearTimeout(watchdog);
            sessionStorage.removeItem(RETRY_KEY);
            setCognitoUser(payload.data);
            setAuthState('ready');
            break;
          case 'signedOut':
            clearTimeout(watchdog);
            setCognitoUser(null);
            setAuthState('ready');
            break;
          case 'signInWithRedirect_failure':
            clearTimeout(watchdog);
            if (isOAuthCallbackUrl) {
              doRetry();
            } else {
              console.error('SSO redirect failed:', payload.data);
              setAuthState('ready');
            }
            break;
          default:
            break;
        }
      });

      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
            userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
            loginWith: {
              oauth: {
                domain: `${process.env.REACT_APP_COGNITO_DOMAIN}.auth.us-west-2.amazoncognito.com`,
                scopes: ['openid', 'email', 'profile'],
                redirectSignIn: [`${window.location.origin}/`],
                redirectSignOut: [`${window.location.origin}/`],
                responseType: 'code',
              },
            },
          },
        },
      });

      signOutRef.current = signOut;

      getCurrentUser()
        .then((u) => {
          clearTimeout(watchdog);
          sessionStorage.removeItem(RETRY_KEY);
          setCognitoUser(u);
          setAuthState('ready');
        })
        .catch(() => {
          // No existing session. If the code is still in the URL, Amplify is
          // processing the callback — watchdog will handle any silent failure.
          if (!window.location.search.includes('code=')) {
            clearTimeout(watchdog);
            setAuthState('ready');
          }
        });
    });

    return () => {
      clearTimeout(watchdog);
      unsubscribe?.();
    };
  }, []);

  const handleSimpleLogout = () => {
    simpleLogout();
    setSimpleLoggedIn(false);
  };

  const handleCognitoLogout = async () => {
    await signOutRef.current?.().catch(console.error);
  };

  const handleSSOClick = async () => {
    const { signInWithRedirect } = await import('aws-amplify/auth');
    // Pass the IdP name so Cognito skips its hosted UI and goes straight to Okta.
    signInWithRedirect({ provider: { custom: 'Okta' } });
  };

  // Break-glass admin session takes priority over everything
  if (simpleLoggedIn) {
    return <AppRoutes user={{ username: getTokenUser() || 'admin' }} onSignOut={handleSimpleLogout} />;
  }

  // Active Cognito (SSO) session
  if (cognitoUser) {
    return <AppRoutes user={cognitoUser} onSignOut={handleCognitoLogout} />;
  }

  // Amplify initialising or processing OAuth callback
  if (authState === 'loading') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 12 }}>
        <Spin size="large" />
        <Typography.Text type="secondary">Signing in…</Typography.Text>
      </div>
    );
  }

  // No session — show combined login page
  return (
    <Login
      onSuccess={() => setSimpleLoggedIn(true)}
      onSSOClick={handleSSOClick}
    />
  );
}

// ─── Root App ──────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <ThemeProvider>
      <ThemedConfigProvider>
        {AUTH_MODE === 'okta' ? <HybridApp /> : <SimpleAuthApp />}
      </ThemedConfigProvider>
    </ThemeProvider>
  );
}
